import type { RefObject } from 'react';
import { CommentIcon } from '../components/icons';
import { cx } from '../components/ui';
import { REACTION_META } from '../lib/reactions';
import { REACTION_KINDS, type ReactionKind } from '../lib/types';
import type { ReactionsState } from './useReactions';

/**
 * The pill under the video: the reactions, a divider, and Comment.
 *
 * Glyphs only, with the word in the label for screen readers and the
 * tooltip. Tapping one never pauses, never seeks, and never moves focus off
 * the video. Comment hands focus to the composer in the side panel.
 */
export function ReactionPill({
  state,
  mediaRef,
  ready,
  onComment,
  compact = false,
  guard,
}: {
  state: ReactionsState;
  mediaRef: RefObject<HTMLMediaElement | null>;
  ready: boolean;
  onComment?: () => void;
  /** Inside the end-of-video card: no frame, no Comment button. */
  compact?: boolean;
  /** Runs the reaction, or asks the visitor to sign in first. */
  guard?: (purpose: 'react' | 'comment', then: () => void) => void;
}) {
  if (state.unavailable) return null;

  const react = (kind: ReactionKind) => {
    // The moment is read now, before any prompt, so a reaction that waits
    // for a sign-in still lands where the tap was.
    const at = mediaRef.current?.currentTime ?? 0;
    const go = () => state.react(kind, at);
    if (guard) guard('react', go);
    else go();
  };

  const buttons = REACTION_KINDS.map((kind) => {
    const meta = REACTION_META[kind];
    const count = state.totals[kind];
    return (
      <button
        key={kind}
        type="button"
        onClick={() => react(kind)}
        disabled={!ready}
        title={meta.label}
        aria-label={count > 0 ? `${meta.label}, ${count} so far` : meta.label}
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'Spacebar') event.stopPropagation();
        }}
        className={cx(
          'relative flex h-10 w-10 items-center justify-center rounded-full text-[20px] leading-none transition-transform duration-120',
          'hover:scale-110 hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none',
          state.lastAccepted === kind && 'scale-110 bg-slate-800',
        )}
      >
        <span aria-hidden="true">{meta.glyph}</span>
        {count > 0 ? (
          <span
            aria-hidden="true"
            className="absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full bg-slate-700 px-1 text-center text-micro tnum text-chalk"
          >
            {count}
          </span>
        ) : null}
      </button>
    );
  });

  if (compact) return <div className="flex items-center justify-center gap-1">{buttons}</div>;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-1 rounded-full border border-hairline bg-slate-900 px-2 py-1.5 shadow-lg shadow-black/30">
        {buttons}
        {onComment ? (
          <>
            <span aria-hidden="true" className="mx-1 h-6 w-px bg-hairline" />
            <button
              type="button"
              onClick={onComment}
              className="flex h-10 items-center gap-2 rounded-full px-3 text-body text-chalk hover:bg-slate-800"
            >
              <CommentIcon size={16} />
              Comment
            </button>
          </>
        ) : null}
      </div>
      {state.postError ? (
        <p role="alert" className="text-caption text-danger">
          {state.postError}
        </p>
      ) : null}
    </div>
  );
}
